/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { neutralGoldTheme } from "@/lib/themes/organization-themes";

/**
 * Default colors - imported from the Neutral Gold theme (single source of truth)
 *
 * IMPORTANT: Do NOT define color values here. All color definitions come from
 * @/lib/themes/organization-themes.ts (neutralGoldTheme)
 *
 * This file simply re-exports the default theme colors for convenience.
 */
export const DEFAULT_PRIMARY_COLOR = neutralGoldTheme.colors.primary.hex;
export const DEFAULT_SECONDARY_COLOR = neutralGoldTheme.colors.secondary.hex;

/**
 * Storage keys for organization theme preferences
 */
export const ORG_THEME_STORAGE_KEY = "org-theme-colors";
export const ORG_THEME_VARS_STORAGE_KEY = "org-theme-vars";

/**
 * CSS variable keys that can be themed
 * These correspond to the CSS custom properties in globals.css
 */
export const THEME_VAR_KEYS = [
  "--primary",
  "--primary-foreground",
  "--primary-50",
  "--primary-100",
  "--primary-200",
  "--primary-300",
  "--primary-400",
  "--primary-500",
  "--primary-600",
  "--primary-700",
  "--primary-800",
  "--primary-900",
  "--primary-950",
  "--secondary",
  "--secondary-foreground",
  "--secondary-50",
  "--secondary-100",
  "--secondary-200",
  "--secondary-300",
  "--secondary-400",
  "--secondary-500",
  "--secondary-600",
  "--secondary-700",
  "--secondary-800",
  "--secondary-900",
  "--secondary-950",
  "--accent",
  "--accent-foreground",
  "--tertiary",
  "--tertiary-foreground",
  "--tertiary-50",
  "--tertiary-100",
  "--tertiary-200",
  "--tertiary-300",
  "--tertiary-400",
  "--tertiary-500",
  "--tertiary-600",
  "--tertiary-700",
  "--tertiary-800",
  "--tertiary-900",
  "--tertiary-950",
  "--tertiary-hover",
  "--tertiary-active",
  "--tertiary-disabled",
  "--ring",
  "--primary-hover",
  "--primary-active",
  "--primary-disabled",
  "--secondary-hover",
  "--secondary-active",
  "--secondary-disabled",
  "--success",
  "--success-foreground",
  "--success-50",
  "--success-100",
  "--success-200",
  "--success-300",
  "--success-400",
  "--success-500",
  "--success-600",
  "--success-700",
  "--success-800",
  "--success-900",
  "--success-950",
  "--warning",
  "--warning-foreground",
  "--warning-50",
  "--warning-100",
  "--warning-200",
  "--warning-300",
  "--warning-400",
  "--warning-500",
  "--warning-600",
  "--warning-700",
  "--warning-800",
  "--warning-900",
  "--warning-950",
  "--error",
  "--error-foreground",
  "--error-50",
  "--error-100",
  "--error-200",
  "--error-300",
  "--error-400",
  "--error-500",
  "--error-600",
  "--error-700",
  "--error-800",
  "--error-900",
  "--error-950",
  "--info",
  "--info-foreground",
  "--info-50",
  "--info-100",
  "--info-200",
  "--info-300",
  "--info-400",
  "--info-500",
  "--info-600",
  "--info-700",
  "--info-800",
  "--info-900",
  "--info-950",
  "--destructive",
  "--destructive-foreground",
  "--background",
  "--foreground",
  "--card",
  "--card-foreground",
  "--border",
  "--input",
  "--muted",
  "--muted-foreground",
  "--surface",
  "--surface-variant",
  "--surface-container-lowest",
  "--surface-container-low",
  "--surface-container",
  "--surface-container-high",
  "--surface-container-highest",
  "--success-hover",
  "--success-active",
  "--success-disabled",
  "--warning-hover",
  "--warning-active",
  "--warning-disabled",
  "--error-hover",
  "--error-active",
  "--error-disabled",
  "--info-hover",
  "--info-active",
  "--info-disabled",
  "--palette-violet",
  "--palette-teal",
  "--palette-pink",
  "--palette-orange",
  "--palette-sky",
] as const;
